/**
 * Landing page for review coaches' first run. Acts as a redirect — the
 * actual tour is mounted by AppShell, so we just kick the coach into
 * the main app. The AppShell-level gate decides whether to display
 * the tour overlay based on `is_review_coach + !first_run_completed_at`
 * or the `?replay=1` query parameter.
 *
 * Why a separate page at all? Because:
 *   1. The magic-link redemption flow lands somewhere specific.
 *   2. The /welcome URL is a stable bookmark — Peter can re-share
 *      `/welcome?replay=1` to re-run the tour.
 *   3. The audio pre-warm runs here so the first TTS chunk plays
 *      without Chrome's autoplay restriction biting.
 */
import { Navigate, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';

export function CoachReviewWelcome(): React.JSX.Element {
  const [params] = useSearchParams();
  const replay = params.get('replay') === '1';

  // Pre-warm the audio output by playing a silent buffer on mount.
  // Bypasses Chrome's autoplay-without-interaction policy that
  // sometimes blocks the first TTS chunk.
  useEffect(() => {
    try {
      const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
    } catch {
      // Audio context unavailable — TTS will still try.
    }
  }, []);

  // Always pass through to /sessions — AppShell decides whether the
  // tour overlay renders. Preserve ?replay=1 so the gate trips.
  const target = replay ? '/sessions?replay=1' : '/sessions';
  return <Navigate to={target} replace />;
}
